import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, 
  FileText, 
  Network, 
  MessageSquare, 
  Calendar,
  ArrowRight,
  CheckCircle,
  Zap,
  Shield,
  Users,
  Crown,
  Infinity,
  HardDrive,
  Sparkles
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();

  const features = [
    {
      icon: FileText,
      title: 'Smart Note Taking',
      description: 'Markdown editor with AI-powered suggestions and automatic linking between related notes.'
    },
    {
      icon: Network,
      title: 'Graph View',
      description: 'Visualize connections between your notes and discover hidden relationships in your knowledge.'
    },
    {
      icon: MessageSquare,
      title: 'AI Chat Assistant',
      description: 'Chat with your notes using DeepSeek AI. Ask questions and get insights from your knowledge base.'
    },
    {
      icon: Calendar,
      title: 'Project Management',
      description: 'Jira-style project tracking with calendar integration and deadline management.'
    }
  ];

  const benefits = [
    'AI-powered note organization',
    'Cross-platform synchronization',
    'Advanced search capabilities',
    'Collaborative workspaces',
    'Export to multiple formats',
    'Privacy-first approach'
  ];

  const pricingPlans = [
    {
      name: 'Gratuito',
      price: '$0',
      period: 'siempre',
      description: 'Perfecto para comenzar',
      features: [
        'Notas ilimitadas',
        '500MB almacenamiento',
        '10 consultas IA/día',
        'Proyectos básicos',
        'Calendario académico',
        'Exportar datos'
      ],
      icon: Zap,
      color: colors.green,
      buttonText: 'Comenzar Gratis',
      popular: false
    },
    {
      name: 'Premium',
      price: '$10',
      period: 'mes',
      annualPrice: '$8',
      description: 'Para estudiantes serios',
      features: [
        'Todo lo del plan gratuito',
        '5GB almacenamiento',
        'IA estándar ilimitada',
        '20 consultas IA premium/día',
        'Integración Google Drive',
        'Soporte prioritario'
      ],
      icon: Crown,
      color: colors.blue,
      buttonText: 'Actualizar a Premium',
      popular: true
    },
    {
      name: 'Unlimited',
      price: '$25',
      period: 'mes',
      annualPrice: '$20',
      description: 'Para uso intensivo',
      features: [
        'Todo lo del plan Premium',
        'Almacenamiento ilimitado',
        'IA premium ilimitada',
        'Colaboración en tiempo real',
        'API personalizada',
        'Soporte 24/7'
      ],
      icon: Infinity,
      color: colors.yellow,
      buttonText: 'Máximo Poder',
      popular: false
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.base }}>
      {/* Navigation */}
      <nav className="px-6 py-4 border-b" style={{ borderColor: colors.surface0 }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8" style={{ color: colors.blue }} />
            <span className="text-2xl font-bold" style={{ color: colors.text }}>
              Enki AI
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-80"
              style={{ color: colors.text }}
            >
              Login
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="px-6 py-2 rounded-lg font-medium text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: colors.blue }}
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="px-6 py-20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-6xl font-bold mb-6 leading-tight"
            style={{ color: colors.text }}
          >
            Your AI-Powered
            <br />
            <span style={{ color: colors.blue }}>Study Companion</span>
          </h1>
          
          <p 
            className="text-xl mb-8 max-w-3xl mx-auto leading-relaxed"
            style={{ color: colors.subtext0 }}
          >
            Transform your learning with intelligent note-taking, project management, 
            and AI-powered insights. Connect ideas, manage projects, and unlock your full potential.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="px-8 py-4 rounded-lg font-medium text-white text-lg transition-all hover:opacity-90 hover:scale-105"
              style={{ backgroundColor: colors.blue }}
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2 inline" />
            </button>
            
            <button
              className="px-8 py-4 rounded-lg font-medium text-lg border-2 transition-all hover:opacity-80"
              style={{ 
                color: colors.text,
                borderColor: colors.surface1,
                backgroundColor: colors.surface0
              }}
            >
              Watch Demo
            </button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="px-6 py-20" style={{ backgroundColor: colors.mantle }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-bold mb-4"
              style={{ color: colors.text }}
            >
              Everything you need to excel
            </h2>
            <p 
              className="text-xl max-w-2xl mx-auto"
              style={{ color: colors.subtext0 }}
            >
              Powerful features designed to enhance your learning and productivity
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="p-6 rounded-xl border transition-all hover:scale-105"
                  style={{ 
                    backgroundColor: colors.base,
                    borderColor: colors.surface0
                  }}
                >
                  <Icon 
                    className="w-12 h-12 mb-4"
                    style={{ color: colors.blue }}
                  />
                  <h3 
                    className="text-xl font-semibold mb-3"
                    style={{ color: colors.text }}
                  >
                    {feature.title}
                  </h3>
                  <p style={{ color: colors.subtext0 }}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-bold mb-4"
              style={{ color: colors.text }}
            >
              Choose your plan
            </h2>
            <p 
              className="text-xl max-w-2xl mx-auto"
              style={{ color: colors.subtext0 }}
            >
              Start free and upgrade as you grow. All plans include our core features.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => {
              const Icon = plan.icon;
              return (
                <div
                  key={index}
                  className={`relative p-8 rounded-2xl border-2 transition-all hover:scale-105 ${
                    plan.popular ? 'ring-2' : ''
                  }`}
                  style={{ 
                    backgroundColor: colors.mantle,
                    borderColor: plan.popular ? colors.blue : colors.surface0,
                    ringColor: plan.popular ? colors.blue : 'transparent'
                  }}
                >
                  {plan.popular && (
                    <div 
                      className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-full text-white text-sm font-medium"
                      style={{ backgroundColor: colors.blue }}
                    >
                      <Crown className="w-4 h-4 inline mr-2" />
                      Más Popular
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <Icon className="w-12 h-12 mx-auto mb-4" style={{ color: plan.color }} />
                    <h3 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>
                      {plan.name}
                    </h3>
                    <p className="mb-6" style={{ color: colors.subtext0 }}>
                      {plan.description}
                    </p>
                    
                    <div className="mb-6">
                      <span className="text-4xl font-bold" style={{ color: colors.text }}>
                        {plan.price}
                      </span>
                      <span className="text-lg" style={{ color: colors.subtext0 }}>
                        /{plan.period}
                      </span>
                      {plan.annualPrice && (
                        <div className="text-sm mt-2" style={{ color: colors.green }}>
                          o {plan.annualPrice}/mes pagando anualmente
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: colors.green }} />
                        <span style={{ color: colors.text }}>{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => navigate('/signup')}
                    className="w-full py-3 rounded-lg font-medium text-white transition-all hover:opacity-90"
                    style={{ backgroundColor: plan.color }}
                  >
                    {plan.buttonText}
                  </button>
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-lg mb-4" style={{ color: colors.text }}>
              ¿Necesitas algo más específico?
            </p>
            <button
              className="px-6 py-3 rounded-lg border-2 font-medium transition-all hover:opacity-80"
              style={{ 
                color: colors.text,
                borderColor: colors.surface1,
                backgroundColor: colors.surface0
              }}
            >
              Contactar Ventas
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-6 py-20" style={{ backgroundColor: colors.mantle }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 
                className="text-4xl font-bold mb-6"
                style={{ color: colors.text }}
              >
                Why choose Enki AI?
              </h2>
              <p 
                className="text-lg mb-8"
                style={{ color: colors.subtext0 }}
              >
                Built for students, researchers, and professionals who want to maximize 
                their learning potential with the power of AI.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle 
                      className="w-5 h-5 flex-shrink-0"
                      style={{ color: colors.green }}
                    />
                    <span style={{ color: colors.text }}>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <div 
                className="p-6 rounded-xl text-center"
                style={{ backgroundColor: colors.surface0 }}
              >
                <Zap className="w-8 h-8 mx-auto mb-3" style={{ color: colors.yellow }} />
                <div className="text-2xl font-bold mb-1" style={{ color: colors.text }}>
                  10x
                </div>
                <div style={{ color: colors.subtext0 }}>Faster Learning</div>
              </div>
              
              <div 
                className="p-6 rounded-xl text-center"
                style={{ backgroundColor: colors.surface0 }}
              >
                <Shield className="w-8 h-8 mx-auto mb-3" style={{ color: colors.blue }} />
                <div className="text-2xl font-bold mb-1" style={{ color: colors.text }}>
                  100%
                </div>
                <div style={{ color: colors.subtext0 }}>Secure</div>
              </div>
              
              <div 
                className="p-6 rounded-xl text-center"
                style={{ backgroundColor: colors.surface0 }}
              >
                <Users className="w-8 h-8 mx-auto mb-3" style={{ color: colors.green }} />
                <div className="text-2xl font-bold mb-1" style={{ color: colors.text }}>
                  50k+
                </div>
                <div style={{ color: colors.subtext0 }}>Active Users</div>
              </div>
              
              <div 
                className="p-6 rounded-xl text-center"
                style={{ backgroundColor: colors.surface0 }}
              >
                <Brain className="w-8 h-8 mx-auto mb-3" style={{ color: colors.mauve }} />
                <div className="text-2xl font-bold mb-1" style={{ color: colors.text }}>
                  AI
                </div>
                <div style={{ color: colors.subtext0 }}>Powered</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="px-6 py-20"
        style={{ backgroundColor: colors.base }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-bold mb-6"
            style={{ color: colors.text }}
          >
            Ready to transform your learning?
          </h2>
          <p 
            className="text-xl mb-8"
            style={{ color: colors.subtext0 }}
          >
            Join thousands of students and professionals who are already using Enki AI 
            to enhance their productivity and learning outcomes.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="px-8 py-4 rounded-lg font-medium text-white text-lg transition-all hover:opacity-90 hover:scale-105"
              style={{ backgroundColor: colors.blue }}
            >
              Get Started for Free
              <ArrowRight className="w-5 h-5 ml-2 inline" />
            </button>
            
            <button
              className="px-8 py-4 rounded-lg font-medium text-lg border-2 transition-all hover:opacity-80"
              style={{ 
                color: colors.text,
                borderColor: colors.surface1,
                backgroundColor: colors.surface0
              }}
            >
              View Pricing
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer 
        className="px-6 py-8 border-t"
        style={{ borderColor: colors.surface0 }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Brain className="w-6 h-6" style={{ color: colors.blue }} />
            <span className="text-lg font-semibold" style={{ color: colors.text }}>
              Enki AI
            </span>
          </div>
          <p style={{ color: colors.subtext0 }}>
            © 2025 Enki AI. Powered by DeepSeek AI. Built for learners, by learners.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;